import React from 'react';
import PropTypes from 'prop-types';
import { AsyncPaginate } from 'react-select-async-paginate';
import styled from 'styled-components';
import { translate } from 'translations/i18n';

import { RESTRICTED } from 'helpers/roles';
import { truncateText } from 'helpers/strings';
import { REACT_SELECT_STYLES, USER_SELECT_STYLES } from 'helpers/styles';
import useTaskListCreateAssignment from 'components/projects/taskList/assign/hooks/useTaskListCreateAssignment.js';
import ImageRestricted from 'images/user_role_restricted.png';
import { useIgnorableConfirmationModal } from 'javascripts/hooks';
import SelectOption from './SelectOption';
import { Notice } from '@companycam/slab-web';
import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';
import useSearchUsersAssignableToTaskList from './gql/useSearchUsersAssignableToTaskList';

// #region ReactSelect
const ReactSelectStyles = {
  ...REACT_SELECT_STYLES,
  ...USER_SELECT_STYLES,
};
const updateInputValue = (newValue) => newValue;
const getOptionValue = ({ id }) => id;
const getFullNameOrEmail = ({ firstName, lastName, emailAddress }) => {
  const fullName = [firstName, lastName].join(' ').trim();
  return fullName || truncateText(emailAddress);
};
const getOptionLabel = ({ firstName, lastName, emailAddress }) =>
  getFullNameOrEmail({ firstName, lastName, emailAddress });
// #endregion

// #region styled-components
const SearchIcon = styled.i`
  color: var(--cc_color_text_nonessential);
  font-size: 22px;
  margin: 0 12px 0 var(--cc_size_spacing_s);
`;
const SelectMessage = styled.div`
  padding: var(--cc_size_spacing_m);
  display: grid;
  gap: var(--cc_size_spacing_s);
  place-items: center;
  text-align: center;
  line-height: 1.25;
`;
const SearchResultsIcon = styled.i`
  font-size: 28px;
  color: var(--cc_color_text_nonessential);
`;
// #endregion

const UserSelect = ({ taskListId, assignedUserIdsSet }) => {
  const { getUsersAssignableToTaskList, error } =
    useSearchUsersAssignableToTaskList();
  const { createTaskListAssignment } = useTaskListCreateAssignment({
    taskListId,
  });

  const modalProps = {
    title: translate(
      'projects.taskList.assign.restrictedUserConfirmationModal.title',
    ),
    image: ImageRestricted,
    message: translate(
      'projects.taskList.assign.restrictedUserConfirmationModal.message',
    ),
    confirmLabel: translate(
      'projects.taskList.assign.restrictedUserConfirmationModal.confirmButton',
    ),
    cancelLabel: translate('common.cancel'),
    destructive: false,
  };

  const assignUserToTaskList = (user) => {
    createTaskListAssignment({
      variables: {
        input: {
          userId: user.id,
          taskListId,
        },
      },
    });

    return user;
  };

  const { doActionOrShowConfirmationModal } = useIgnorableConfirmationModal({
    confirmationKey: 'assignRestrictedUserToChecklist',
    confirmationAction: assignUserToTaskList,
    modalProps,
  });

  const loadUsersAssignableToTaskList = async (query, _, additional = {}) => {
    const results = await getUsersAssignableToTaskList({
      taskListId,
      query,
      after: additional.endCursor,
    });

    return results;
  };

  const handleAssignUser = (user) => {
    // TODO[Jose]: handle clearing of input, etc.
    if (user.userRole === RESTRICTED) {
      doActionOrShowConfirmationModal(user, {
        subtitle: translate(
          'projects.taskList.assign.restrictedUserConfirmationModal.subtitle',
          {
            name: getFullNameOrEmail({
              firstName: user.firstName,
              lastName: user.lastName,
              emailAddress: user.emailAddress,
            }),
          },
        ),
      });
    } else {
      assignUserToTaskList(user);
    }
  };

  const getIsOptionSelected = (user) => assignedUserIdsSet.has(user.id);

  return (
    <AsyncPaginate
      id="users-assignable-to-tasklist"
      name="search-tasklist-users"
      cacheOptions
      value={null}
      debounceTimeout={250}
      loadOptions={loadUsersAssignableToTaskList}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onInputChange={updateInputValue}
      onChange={handleAssignUser}
      isOptionSelected={getIsOptionSelected}
      hideSelectedOptions
      noOptionsMessage={() =>
        error ? (
          <Notice color="destroy" size="small">
            {translate('project.tasklists.assign.searchErrorMessage')}
          </Notice>
        ) : (
          <SelectMessage>
            <SearchResultsIcon className="mdi mdi-scan-helper" />
            <Text>
              {translate('project.tasklists.assign.searchNoResultsMessage')}
            </Text>
          </SelectMessage>
        )
      }
      placeholder={translate('project.tasklists.assign.searchInputPlaceholder')}
      menuPortalTarget={document.body}
      styles={ReactSelectStyles}
      components={{
        DropdownIndicator: () => (
          <SearchIcon className="mdi mdi-magnify" aria-hidden="true" />
        ),
        LoadingMessage: () => (
          <div style={{ padding: 8 }}>
            <SelectMessage>
              <SearchResultsIcon className="mdi mdi-face-recognition" />
              <Text>
                {translate('project.tasklists.assign.searchInProgressMessage')}
              </Text>
            </SelectMessage>
          </div>
        ),
        IndicatorSeparator: () => null,
        Option: ({
          /* eslint-disable react/prop-types */
          data: { firstName, lastName, emailAddress, userRole },
          innerRef,
          innerProps,
          isFocused,
          isSelected,
          /* eslint-enable react/prop-types */
        }) => (
          <SelectOption
            innerRef={innerRef}
            innerProps={innerProps}
            primaryDescription={getFullNameOrEmail({
              firstName,
              lastName,
              emailAddress,
            })}
            secondaryDescription={userRole === RESTRICTED ? userRole : ''}
            isFocused={isFocused}
            isSelected={isSelected}
          />
        ),
      }}
    />
  );
};

UserSelect.propTypes = {
  taskListId: PropTypes.string.isRequired,
  assignedUserIdsSet: PropTypes.object.isRequired,
};

export default UserSelect;
